import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Header,
  SpaceBetween,
  Box,
  Button,
  Table,
  TextFilter,
  Pagination,
  Flashbar,
  FlashbarProps,
  Modal,
  Toggle,
  ButtonDropdown,
} from "@cloudscape-design/components";
import {
  ProgramData,
  ProgramStatus,
  EditMode,
  CreationMethod,
  BulkEditResult,
  ColumnVisibility,
  ColumnId,
  ALL_COLUMNS,
  DEFAULT_COLUMN_VISIBILITY,
} from "../../types/configurationTypes";
import { RootState } from "../../redux/reducers/rootReducer";
import {
  getProgramsRequest,
  createProgramRequest,
  updateProgramsRequest,
  updateProgramsStatusRequest,
  getProductFamiliesRequest,
  getProgramOptionsRequest,
  clearConfigurationResults,
} from "../../redux/actions/configurationActions";
import ProgramWizard from "./ProgramWizard";
import { ALL_COLUMN_DEFINITIONS, BUILT_IN_CATEGORY_SUB_ITEMS, buildCustomTargetColumnDefinition } from "./columnDefinitions";
import { usePermission } from "../../hooks/usePermission";
import "./ConfigurationSetting.css";

// ─── Main Component ─────────────────────────────────────────────────────────────
const ConfigurationSetting: React.FC = () => {
  const dispatch = useDispatch();
  const { hasPermission, hasDataPermission } = usePermission();

  // ─── Redux State ────────────────────────────────────────────────────────────
  const {
    programs,
    programsLoading,
    programsError,
    createProgramLoading,
    createProgramResult,
    createProgramError,
    updateProgramsLoading,
    updateProgramsResult,
    updateProgramsError,
    updateStatusLoading,
    updateStatusResult,
    updateStatusError,
    productFamilies,
    productFamiliesError,
    programOptions,
    programOptionsError,
  } = useSelector((state: RootState) => state.configuration);

  // ─── Use API data directly as ProgramData (no mapping needed) ───────────────
  const programList: ProgramData[] = programs as ProgramData[];

  // ─── Track whether this is the initial mount ────────────────────────────────
  // Used to skip reacting to stale Redux result/error values on first render
  const isInitialMountRef = useRef(true);

  // ─── Load data on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    // Clear stale result/error state from previous session to prevent
    // useEffect handlers from re-triggering flash notifications on re-mount
    dispatch(clearConfigurationResults());
    dispatch(getProgramsRequest());
    dispatch(getProductFamiliesRequest());
    dispatch(getProgramOptionsRequest());

    // Mark initial mount as complete after this tick so that
    // response-watching effects skip the stale values from the first render
    const timer = setTimeout(() => {
      isInitialMountRef.current = false;
    }, 0);
    return () => clearTimeout(timer);
  }, [dispatch]);

  // UI state
  const [currentView, setCurrentView] = useState<"list" | "wizard">("list");
  const [editMode, setEditMode] = useState<EditMode>("create");
  const [creationMethod, setCreationMethod] = useState<CreationMethod>("scratch");
  const [selectedItems, setSelectedItems] = useState<ProgramData[]>([]);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [sortingColumn, setSortingColumn] = useState<{ sortingField?: string }>({});
  const [sortingDescending, setSortingDescending] = useState(false);

  // Separate state for batch failure detail notification — rendered in normal document flow, no auto-dismiss
  const [batchFailureFlashItems, setBatchFailureFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Confirmation modal state for enable/disable actions
  const [actionConfirmVisible, setActionConfirmVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ newStatus: ProgramStatus; itemsToUpdate: ProgramData[]; skippedCount: number } | null>(null);

  // Track failed program IDs from batch update — used to visually distinguish failed rows in the table
  const [failedProgramIds, setFailedProgramIds] = useState<Set<string>>(new Set());

  // Column visibility state (session-based, not persisted)
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(
    DEFAULT_COLUMN_VISIBILITY
  );
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [tempColumnVisibility, setTempColumnVisibility] = useState<ColumnVisibility>(
    DEFAULT_COLUMN_VISIBILITY
  );

  // Custom target column visibility — keyed by generated column id (dynamic, session-based)
  const [customColumnVisibility, setCustomColumnVisibility] = useState<Record<string, boolean>>({});
  const [tempCustomColumnVisibility, setTempCustomColumnVisibility] = useState<Record<string, boolean>>({});

  // Data-level permission: check if all selected items allow the given action
  const canUpdateSelected = useMemo(() => {
    if (selectedItems.length === 0) return false;
    return selectedItems.every((item) =>
      hasDataPermission("configuration_setting", "cs_update", {
        cm: item.cm,
        location: item.cm_location,
        program: item.program_name,
        operation: item.operation,
      })
    );
  }, [selectedItems, hasDataPermission]);

  const canActionSelected = useMemo(() => {
    if (selectedItems.length === 0) return false;
    return selectedItems.every((item) =>
      hasDataPermission("configuration_setting", "cs_actions", {
        cm: item.cm,
        location: item.cm_location,
        program: item.program_name,
        operation: item.operation,
      })
    );
  }, [selectedItems, hasDataPermission]);

  const PAGE_SIZE = 10;
  const BATCH_SIZE = 2; // Max records per batch update request

  // ─── Batch Update Tracking (sequential dispatch) ────────────────────────────
  const batchQueueRef = useRef<Array<ReturnType<typeof updateProgramsRequest>>>([]);
  const batchResultsRef = useRef<{ success: number; failure: number; failedPrograms: Array<{ program_id?: string; program_name?: string; cm?: string; cm_location?: string; error?: string }> }>({
    success: 0,
    failure: 0,
    failedPrograms: [],
  });
  const isBatchingRef = useRef(false);
  // Preserve selectedItems snapshot during batch so we can enrich failed_programs with cm/cm_location
  // Also used for single updates to display program names in success messages
  const batchSelectedItemsRef = useRef<ProgramData[]>([]);

  // ─── Flash auto-dismiss timer ───────────────────────────────────────────────
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showFlash = useCallback((items: FlashbarProps.MessageDefinition[]) => {
    // Clear any existing timer
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
    // Set flash items with onDismiss wired up
    setFlashItems(
      items.map((item) => ({
        ...item,
        dismissible: true,
        onDismiss: (event) => {
          item.onDismiss?.(event);
          setFlashItems([])
        },
      }))
    );
    // Auto-dismiss after 10 seconds
    flashTimerRef.current = setTimeout(() => {
      setFlashItems([]);
      flashTimerRef.current = null;
    }, 10000);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
    };
  }, []);

  // ─── Handle API response effects ───────────────────────────────────────────
  // Handle create program result
  useEffect(() => {
    if (isInitialMountRef.current) return;
    if (createProgramResult) {
      showFlash([
        {
          type: "success",
          content: createProgramResult.message || "Program created successfully.",
          id: "create-success",
        },
      ]);
      // Hide the wizard and go back to list view on success
      setCurrentView("list");
      setSelectedItems([]);
      // Refresh the programs list
      dispatch(getProgramsRequest());
    }
  }, [createProgramResult, dispatch, showFlash]);

  useEffect(() => {
    if (isInitialMountRef.current) return;
    if (createProgramError) {
      showFlash([
        {
          type: "error",
          content: `Failed to create program: ${createProgramError}`,
          id: "create-error",
        },
      ]);
      // Hide the wizard and go back to list view on error
      setCurrentView("list");
      setSelectedItems([]);
    }
  }, [createProgramError, showFlash]);

  /** Build a React node listing failed programs with Program Name, CM, and cm_location */
  const buildFailedProgramsDetail = useCallback(
    (failedPrograms: Array<{ program_id?: string; program_name?: string; cm?: string; cm_location?: string; error?: string }>) => {
      // Enrich failed programs with cm/cm_location from the batch snapshot
      const enriched = failedPrograms.map((fp) => {
        const match = batchSelectedItemsRef.current.find((p) => p.program_id === fp.program_id);
        return {
          program_name: fp.program_name || match?.program_name || fp.program_id || "Unknown",
          cm: fp.cm || match?.cm || "—",
          cm_location: fp.cm_location || match?.cm_location || "—",
          error: fp.error || "",
        };
      });

      return (
        <div>
          <div style={{ marginBottom: 6 }}>Failed programs:</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {enriched.map((fp, idx) => (
              <li key={idx} style={{ marginBottom: 4 }}>
                <strong>{fp.program_name}</strong> — CM: {fp.cm}, Location: {fp.cm_location}
                {fp.error && (
                  <span style={{ marginTop: 2, marginLeft: 4, fontSize: '0.9em', fontStyle: 'italic' }}>
                    <span style={{paddingLeft:10,color:'#fbbebe'}}>
                      {fp.error.includes('Version conflict')? 
                      <>
                        (This data has been modified by another user. 
                        <strong style={{ fontStyle: 'normal' }}>Please refresh and try again.)</strong>
                      </>
                      : `(${fp.error})`}
                      </span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    },
    []
  );

  /** Dispatch the next batch in the queue, or finalize if queue is empty */
  const processNextBatch = useCallback(() => {
    if (batchQueueRef.current.length > 0) {
      const nextAction = batchQueueRef.current.shift()!;
      dispatch(nextAction);
    } else {
      // All batches completed — show aggregated result
      isBatchingRef.current = false;
      const accumulated = batchResultsRef.current;

      // Track failed program IDs for table row highlighting
      if (accumulated.failedPrograms.length > 0) {
        const ids = new Set(accumulated.failedPrograms.map((fp) => fp.program_id).filter(Boolean) as string[]);
        setFailedProgramIds(ids);
      } else {
        setFailedProgramIds(new Set());
      }
      if (accumulated.failure > 0) {
      // Show in-flow notification (no auto-dismiss) for partial failure details
      setBatchFailureFlashItems([
        {
          type: "error",
          content: (
            <div>
              <div>Batch update completed: {accumulated.success} succeeded, {accumulated.failure} failed.</div>
              {buildFailedProgramsDetail(accumulated.failedPrograms)}
            </div>
          ),
          id: "update-partial",
          dismissible: true,
          onDismiss: () => {
            setBatchFailureFlashItems([]);
            setFailedProgramIds(new Set());
          },
        },
      ]);
      } else {
        const updatedNames = batchSelectedItemsRef.current.map((p) => p.program_name).join(", ");
        showFlash([
          {
            type: "success",
            content: `Batch update completed successfully. ${accumulated.success} program(s) updated: ${updatedNames}.`,
            id: "update-success",
          },
        ]);
      }
      // Reset accumulated results
      batchResultsRef.current = { success: 0, failure: 0, failedPrograms: [] };
      batchSelectedItemsRef.current = [];
      // Hide the wizard and go back to list view after batch completes
      setCurrentView("list");
      setSelectedItems([]);
      // Refresh the programs list
      dispatch(getProgramsRequest());
    }
  }, [dispatch, showFlash, buildFailedProgramsDetail]);

  // Handle update programs result (supports sequential batch accumulation)
  useEffect(() => {
    if (isInitialMountRef.current) return;
    if (updateProgramsResult) {
      if (isBatchingRef.current) {
        // Accumulate this batch's results
        batchResultsRef.current.success += updateProgramsResult.success || 0;
        batchResultsRef.current.failure += updateProgramsResult.failure || 0;
        if (updateProgramsResult.failed_programs?.length > 0) {
          batchResultsRef.current.failedPrograms.push(...updateProgramsResult.failed_programs);
        }
        // Process next batch or finalize
        processNextBatch();
      } else {
        // Non-batched (single update) — show detailed info for failures
        if (updateProgramsResult.failure > 0) {
          // Track failed IDs for table row highlighting
          const failedIds = new Set(
            updateProgramsResult.failed_programs
              .map((fp) => fp.program_id)
              .filter(Boolean)
          );
          setFailedProgramIds(failedIds);

          // Show in-flow notification (no auto-dismiss) for failure details
          setBatchFailureFlashItems([
            {
              type: "error",
              content: (
                <div>
                  <div>Update completed: {updateProgramsResult.success} succeeded, {updateProgramsResult.failure} failed.</div>
                  {buildFailedProgramsDetail(updateProgramsResult.failed_programs)}
                </div>
              ),
              id: "update-partial",
              dismissible: true,
              onDismiss: () => {
                setBatchFailureFlashItems([]);
                setFailedProgramIds(new Set());
              },
            },
          ]);
        } else {
          const updatedNames = batchSelectedItemsRef.current.map((p) => p.program_name).join(", ");
          showFlash([
            {
              type: "success",
              content: `Update completed successfully. ${updateProgramsResult.success} program(s) updated: ${updatedNames}.`,
              id: "update-success",
            },
          ]);
        }
        // Clear the snapshot after use
        batchSelectedItemsRef.current = [];
        // Hide the wizard and go back to list view after update completes
        setCurrentView("list");
        setSelectedItems([]);
        // Refresh the programs list
        dispatch(getProgramsRequest());
      }
    }
  }, [updateProgramsResult, dispatch, showFlash, processNextBatch]);

  useEffect(() => {
    if (isInitialMountRef.current) return;
    if (updateProgramsError) {
      if (isBatchingRef.current) {
        // Accumulate failure for this batch
        batchResultsRef.current.failure += 1;
        // Process next batch or finalize
        processNextBatch();
      } else {
        // Non-batched — original behavior
        showFlash([
          {
            type: "error",
            content: `Failed to update programs: ${updateProgramsError}`,
            id: "update-error",
          },
        ]);
        // Hide the wizard and go back to list view on error
        setCurrentView("list");
        setSelectedItems([]);
      }
    }
  }, [updateProgramsError, showFlash, processNextBatch]);

  // Handle update status result
  useEffect(() => {
    if (isInitialMountRef.current) return;
    if (updateStatusResult) {
      showFlash([
        {
          type: "success",
          content: updateStatusResult.message,
          id: "status-success",
        },
      ]);
      console.log('updateStatusResult',updateStatusResult)
      // Close the confirmation modal after API success
      setActionConfirmVisible(false);
      setPendingAction(null);
      // Refresh the programs list
      dispatch(getProgramsRequest());
    }
  }, [updateStatusResult, dispatch, showFlash]);

  useEffect(() => {
    if (isInitialMountRef.current) return;
    if (updateStatusError) {
      showFlash([
        {
          type: "error",
          content: `Failed to update status: ${updateStatusError}`,
          id: "status-error",
        },
      ]);
      // Close the confirmation modal after API error
      setActionConfirmVisible(false);
      setPendingAction(null);
    }
  }, [updateStatusError, showFlash]);

  // Handle programs loading error
  useEffect(() => {
    if (programsError) {
      showFlash([
        {
          type: "error",
          content: `Failed to load programs: ${programsError}`,
          id: "load-error",
        },
      ]);
    }
  }, [programsError, showFlash]);

  // Handle program options loading error (Auto Report API)
  useEffect(() => {
    if (programOptionsError) {
      showFlash([
        {
          type: "error",
          content: `Failed to load program options: ${programOptionsError}. The Program, CM, Location, and Operation dropdowns may be empty.`,
          id: "program-options-error",
        },
      ]);
    }
  }, [programOptionsError, showFlash]);

  // Handle product families loading error
  useEffect(() => {
    if (productFamiliesError) {
      showFlash([
        {
          type: "error",
          content: `Failed to load product families: ${productFamiliesError}. The Product Family dropdown may be empty.`,
          id: "product-families-error",
        },
      ]);
    }
  }, [productFamiliesError, showFlash]);

  // ─── Custom Target Columns (derived from program data) ──────────────────────
  // Scan all programs to find custom category_subItem values not covered by built-in columns
  const customTargetCategories = useMemo(() => {
    const customSet = new Set<string>();
    programList.forEach((program) => {
      program.target_settings?.forEach((ts) => {
        if (!BUILT_IN_CATEGORY_SUB_ITEMS.includes(ts.category_subItem)) {
          customSet.add(ts.category_subItem);
        }
      });
    });
    return Array.from(customSet).sort();
  }, [programList]);

  // Build dynamic column definitions for custom target categories
  const customTargetColumnDefinitions = useMemo(() => {
    return customTargetCategories.map((category) => buildCustomTargetColumnDefinition(category));
  }, [customTargetCategories]);

  // Ensure custom column visibility state includes entries for all discovered custom categories
  // (new categories default to hidden/false)
  useEffect(() => {
    if (customTargetCategories.length === 0) return;
    setCustomColumnVisibility((prev) => {
      const updated = { ...prev };
      let changed = false;
      customTargetCategories.forEach((category) => {
        const colId = `custom_target_${category.replace(/[^a-zA-Z0-9]/g, "_")}`;
        if (!(colId in updated)) {
          updated[colId] = false;
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [customTargetCategories]);

  // Visible column definitions based on column visibility settings,
  // with failed row styling applied to cells of programs that failed batch update
  const visibleColumnDefinitions = useMemo(() => {
    // Filter built-in columns by visibility
    const baseCols = ALL_COLUMN_DEFINITIONS.filter(
      (col) => columnVisibility[col.id as ColumnId]
    );

    // Filter custom target columns by their visibility
    const visibleCustomCols = customTargetColumnDefinitions.filter(
      (col) => customColumnVisibility[col.id]
    );

    // Merge built-in + custom columns
    const allCols = [...baseCols, ...visibleCustomCols];

    // If no failed programs, return columns as-is
    if (failedProgramIds.size === 0) return allCols;

    // Wrap each column's cell renderer to add failed-row styling
    return allCols.map((col) => ({
      ...col,
      cell: (item: ProgramData) => {
        const originalCell = col.cell(item);
        if (col.id !== "status" && failedProgramIds.has(item.program_id)) {
          return <span className="row-update-failed">{originalCell}</span>;
        }
        return originalCell;
      },
    }));
  }, [columnVisibility, customColumnVisibility, customTargetColumnDefinitions, failedProgramIds]);

  // Filtered data
  const filteredPrograms = useMemo(() => {
    if (!filterText) return programList;
    const lower = filterText.toLowerCase();
    return programList.filter(
      (p) =>
        p.program_name.toLowerCase().includes(lower) ||
        p.cm.toLowerCase().includes(lower) ||
        p.cm_location.toLowerCase().includes(lower) ||
        p.operation.toLowerCase().includes(lower) ||
        p.product_family_name.toLowerCase().includes(lower) ||
        (p.program_mapping && p.program_mapping.some((sub) => sub.toLowerCase().includes(lower)))
    );
  }, [programList, filterText]);

  const visiblePrograms = filteredPrograms;

  // Sorted data
  const sortedPrograms = useMemo(() => {
    if (!sortingColumn.sortingField) return visiblePrograms;
    const field = sortingColumn.sortingField as keyof ProgramData;
    const sorted = [...visiblePrograms].sort((a, b) => {
      const aVal = a[field] ?? "";
      const bVal = b[field] ?? "";
      if (typeof aVal === "string" && typeof bVal === "string") {
        return aVal.localeCompare(bVal);
      }
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortingDescending ? sorted.reverse() : sorted;
  }, [visiblePrograms, sortingColumn, sortingDescending]);

  // Paginated data
  const paginatedPrograms = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedPrograms.slice(start, start + PAGE_SIZE);
  }, [sortedPrograms, currentPage]);

  const totalPages = Math.ceil(visiblePrograms.length / PAGE_SIZE);

  // ─── Settings Modal Handlers ────────────────────────────────────────────────
  const handleOpenSettings = useCallback(() => {
    setTempColumnVisibility({ ...columnVisibility });
    setTempCustomColumnVisibility({ ...customColumnVisibility });
    setSettingsModalVisible(true);
  }, [columnVisibility, customColumnVisibility]);

  const handleConfirmSettings = useCallback(() => {
    setColumnVisibility({ ...tempColumnVisibility });
    setCustomColumnVisibility({ ...tempCustomColumnVisibility });
    setSettingsModalVisible(false);
  }, [tempColumnVisibility, tempCustomColumnVisibility]);

  const handleCancelSettings = useCallback(() => {
    setSettingsModalVisible(false);
  }, []);

  const handleToggleColumn = useCallback(
    (columnId: ColumnId, checked: boolean) => {
      setTempColumnVisibility((prev) => ({
        ...prev,
        [columnId]: checked,
      }));
    },
    []
  );

  const handleToggleAllBasicColumns = useCallback(
    (checked: boolean) => {
      setTempColumnVisibility((prev) => {
        const updated = { ...prev };
        const basicCols = ALL_COLUMNS.filter((col) => !col.group);
        basicCols.forEach((col) => {
          if (!col.alwaysVisible) {
            updated[col.id] = checked;
          }
        });
        return updated;
      });
    },
    []
  );

  const handleToggleAllTargetColumns = useCallback(
    (checked: boolean) => {
      setTempColumnVisibility((prev) => {
        const updated = { ...prev };
        const targetCols = ALL_COLUMNS.filter((col) => col.group === "Target Settings");
        targetCols.forEach((col) => {
          updated[col.id] = checked;
        });
        return updated;
      });
    },
    []
  );

  // Custom target column toggle handler
  const handleToggleCustomColumn = useCallback(
    (colId: string, checked: boolean) => {
      setTempCustomColumnVisibility((prev) => ({
        ...prev,
        [colId]: checked,
      }));
    },
    []
  );

  // Toggle all custom target columns
  const handleToggleAllCustomTargetColumns = useCallback(
    (checked: boolean) => {
      setTempCustomColumnVisibility((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          updated[key] = checked;
        });
        return updated;
      });
    },
    []
  );

  // ─── Onboard Handler ───────────────────────────────────────────────────────
  const handleOnboardProgram = useCallback(() => {
    setEditMode("create");
    setCreationMethod("scratch");
    setSelectedItems([]);
    setCurrentView("wizard");
  }, []);

  // ─── Update Handler (auto-detect single/batch) ─────────────────────────────
  const MAX_BULK_EDIT_COUNT = 10;

  const handleUpdate = useCallback(() => {
    if (selectedItems.length === 0) return;
    if (selectedItems.length > MAX_BULK_EDIT_COUNT) {
      showFlash([
        {
          type: "error",
          content: `Batch update supports a maximum of ${MAX_BULK_EDIT_COUNT} programs at a time. You have selected ${selectedItems.length} programs. Please reduce your selection.`,
          id: "bulk-edit-limit",
        },
      ]);
      return;
    }
    if (selectedItems.length === 1) {
      setEditMode("editSingle");
    } else {
      setEditMode("editBulk");
    }
    setCreationMethod("scratch");
    setCurrentView("wizard");
  }, [selectedItems, showFlash]);

  // ─── Action Dropdown Handler (Enable/Disable) ──────────────────────────────
  const handleActionClick = useCallback(
    (actionId: string) => {
      if (selectedItems.length === 0) return;

      const newStatus: ProgramStatus =
        actionId === "enable" ? "enabled" : "disabled";

      // Filter out items that already match the target status
      const itemsToUpdate = selectedItems.filter(
        (p) => p.status !== newStatus
      );

      // If all selected items already have the target status, notify the user
      if (itemsToUpdate.length === 0) {
        showFlash([
          {
            type: "info",
            content: `All selected program(s) are already ${newStatus}. No action is required.`,
            id: "status-no-change",
            onDismiss: () => {
              setSelectedItems([])
            },
          },
        ]);
        return;
      }

      const skippedCount = selectedItems.length - itemsToUpdate.length;

      // Show confirmation modal instead of directly dispatching
      setPendingAction({ newStatus, itemsToUpdate, skippedCount });
      setActionConfirmVisible(true);
    },
    [selectedItems, showFlash]
  );

  // ─── Confirm Action Handler ─────────────────────────────────────────────────
  const handleConfirmAction = useCallback(() => {
    if (!pendingAction) return;

    const { newStatus, itemsToUpdate, skippedCount } = pendingAction;

    const programItems = itemsToUpdate.map((p) => ({ program_id: p.program_id, version: p.version }));

    // Dispatch API call to update status
    dispatch(
      updateProgramsStatusRequest({
        program_ids: programItems,
        status: newStatus,
      })
    );

    setSelectedItems([]);
  }, [pendingAction, dispatch]);

  const handleCancelAction = useCallback(() => {
    setActionConfirmVisible(false);
    setPendingAction(null);
  }, []);

  // ─── Wizard Handlers ───────────────────────────────────────────────────────
  const handleWizardCancel = useCallback(() => {
    setCurrentView("list");
    setSelectedItems([]);
  }, []);

  const handleWizardSubmit = useCallback(
    (
      mode: EditMode,
      data: {
        basic_info: Record<string, any>;
        target_settings: Array<{ category_subItem: string; target_value?: number; dri?: string }>;
        delete_target_settings?: string[];
      },
      bulkProgramIds?: string[]
    ) => {
      if (mode === "create") {
        dispatch(createProgramRequest(data));
      } else if (mode === "editSingle") {
        // Update single program via API
        const program = selectedItems[0];
        if (program) {
          // Snapshot selected items for success message display
          batchSelectedItemsRef.current = [...selectedItems];
          const payload = {
            program_ids: [{ program_id: program.program_id, version: program.version }],
            basic_info: data.basic_info,
            target_settings: data.target_settings,
            ...(data.delete_target_settings && data.delete_target_settings.length > 0
              ? { delete_target_settings: data.delete_target_settings }
              : {}),
          };
          dispatch(updateProgramsRequest(payload));
        }
      } else if (mode === "editBulk") {
        // Batch update via API — split into batches of BATCH_SIZE records each
        const programItems = selectedItems.map((p) => ({ program_id: p.program_id, version: p.version }));
        const batchActions: Array<ReturnType<typeof updateProgramsRequest>> = [];
        for (let i = 0; i < programItems.length; i += BATCH_SIZE) {
          const batchItems = programItems.slice(i, i + BATCH_SIZE);
          const payload = {
            program_ids: batchItems,
            basic_info: data.basic_info,
            target_settings: data.target_settings,
            ...(data.delete_target_settings && data.delete_target_settings.length > 0
              ? { delete_target_settings: data.delete_target_settings }
              : {}),
          };
          batchActions.push(updateProgramsRequest(payload));
        }

        // Initialize batch tracking and queue
        isBatchingRef.current = true;
        batchResultsRef.current = { success: 0, failure: 0, failedPrograms: [] };
        batchSelectedItemsRef.current = [...selectedItems]; // Snapshot for enriching failed_programs later
        batchQueueRef.current = batchActions.slice(1); // Queue all except the first
        // Clear previous failed program highlights and in-flow failure notification
        setFailedProgramIds(new Set());
        setBatchFailureFlashItems([]);

        // Dispatch the first batch to start the sequential chain
        if (batchActions.length > 0) {
          dispatch(batchActions[0]);
        }
      }

    },
    [selectedItems, dispatch]
  );

  // ─── Render: Wizard View ────────────────────────────────────────────────────
  if (currentView === "wizard") {
    return (
      <div className="configuration-setting">
        <ProgramWizard
          mode={editMode}
          creationMethod={creationMethod}
          selectedPrograms={selectedItems}
          allPrograms={programList}
          onCancel={handleWizardCancel}
          onSubmit={handleWizardSubmit}
          productFamilies={productFamilies}
          programOptions={programOptions}
          submitLoading={createProgramLoading || updateProgramsLoading}
        />
      </div>
    );
  }

  // ─── Render: Settings Modal ─────────────────────────────────────────────────
  const basicColumns = ALL_COLUMNS.filter((col) => !col.group);
  const targetColumns = ALL_COLUMNS.filter((col) => col.group === "Target Settings");

  const renderSettingsModal = () => (
    <Modal
      visible={settingsModalVisible}
      onDismiss={handleCancelSettings}
      header="Column Settings"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <span data-testid="column-settings-cancel">
              <Button variant="link" onClick={handleCancelSettings}>
                Cancel
              </Button>
            </span>
            <span data-testid="column-settings-confirm">
              <Button variant="primary" onClick={handleConfirmSettings}>
                Confirm
              </Button>
            </span>
          </SpaceBetween>
        </Box>
      }
    >
      <div className="column-settings-modal-content">
      <SpaceBetween size="s">
        <Box variant="p" color="text-body-secondary" fontSize="body-s">
          <span id="column-setting-header-description">
            Select which columns to display in the program list. Changes are
            session-based and will not be persisted.
          </span>
        </Box>

        <div className="column-settings-section">
          <div className="column-settings-header">
            <Box variant="h4">Basic Information</Box>
            <span data-testid="column-settings-basic-select-all">
              <Toggle
                checked={basicColumns.filter((col) => !col.alwaysVisible).every((col) => tempColumnVisibility[col.id])}
                onChange={({ detail }) => handleToggleAllBasicColumns(detail.checked)}
              >
                Select All
              </Toggle>
            </span>
          </div>
          <div className="column-settings-grid">
            {basicColumns.map((col) => (
              <div key={col.id} className="column-settings-item" id={`column-setting-toggle-${col.id}`}>
                <Toggle
                  checked={tempColumnVisibility[col.id]}
                  onChange={({ detail }) =>
                    handleToggleColumn(col.id, detail.checked)
                  }
                  disabled={col.alwaysVisible}
                >
                  {col.label}
                  {col.alwaysVisible && (
                    <span className="form-field-optional"> (always visible)</span>
                  )}
                </Toggle>
              </div>
            ))}
          </div>
        </div>

        <div className="column-settings-section">
          <div className="column-settings-header">
            <Box variant="h4">Default Target Settings</Box>
            <span data-testid="column-settings-target-select-all">
              <Toggle
                checked={targetColumns.every((col) => tempColumnVisibility[col.id])}
                onChange={({ detail }) => handleToggleAllTargetColumns(detail.checked)}
              >
                Select All
              </Toggle>
            </span>
          </div>
          <div className="column-settings-grid">
            {targetColumns.map((col) => (
              <div key={col.id} className="column-settings-item" id={`column-setting-toggle-${col.id}`}>
                <Toggle
                  checked={tempColumnVisibility[col.id]}
                  onChange={({ detail }) =>
                    handleToggleColumn(col.id, detail.checked)
                  }
                >
                  {col.label}
                </Toggle>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Target Settings — dynamically discovered from program data */}
        {customTargetColumnDefinitions.length > 0 && (
          <div className="column-settings-section">
            <div className="column-settings-header">
              <Box variant="h4">Custom Target Settings</Box>
              <span data-testid="column-settings-custom-target-select-all">
                <Toggle
                  checked={customTargetColumnDefinitions.every((col) => tempCustomColumnVisibility[col.id])}
                  onChange={({ detail }) => handleToggleAllCustomTargetColumns(detail.checked)}
                >
                  Select All
                </Toggle>
              </span>
            </div>
            <div className="column-settings-grid">
              {customTargetColumnDefinitions.map((col) => (
                <div key={col.id} className="column-settings-item" id={`column-setting-toggle-${col.id}`} data-testid="column-settings-custom-target-item">
                  <Toggle
                    checked={tempCustomColumnVisibility[col.id] || false}
                    onChange={({ detail }) =>
                      handleToggleCustomColumn(col.id, detail.checked)
                    }
                  >
                    {col.header}
                  </Toggle>
                </div>
              ))}
            </div>
          </div>
        )}
      </SpaceBetween>
      </div>
    </Modal>
  );


  // ─── Render: Program List View ──────────────────────────────────────────────
  return (
    <div className="configuration-setting" data-testid="configuration-setting-page">
      {/* Floating flashbar - absolutely positioned, does not take up page space */}
      {flashItems.length > 0 && (
        <div className="flashbar-floating-container">
          <Flashbar items={flashItems} />
        </div>
      )}

      {/* Confirmation Modal for Enable/Disable Actions */}
      <Modal
        visible={actionConfirmVisible}
        onDismiss={handleCancelAction}
        header={`Confirm ${pendingAction?.newStatus === "enabled" ? "Enable" : "Disable"}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancelAction} disabled={updateStatusLoading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmAction}
                loading={updateStatusLoading}
                disabled={updateStatusLoading}
              >
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box variant="p">
          Are you sure you want to {pendingAction?.newStatus === "enabled" ? "enable" : "disable"}{" "}
          {pendingAction?.itemsToUpdate.length} program(s)?
          {pendingAction && pendingAction.skippedCount > 0 && (
            <Box variant="p" color="text-body-secondary" padding={{ top: "xs" }}>
              Note: {pendingAction.skippedCount} program(s) already{" "}
              {pendingAction.newStatus} will be skipped.
            </Box>
          )}
        </Box>
      </Modal>

      <SpaceBetween size="l">
        {renderSettingsModal()}

        {/* In-flow batch failure notification — occupies normal document flow, no auto-dismiss */}
        {batchFailureFlashItems.length > 0 && (
          <Flashbar items={batchFailureFlashItems} />
        )}

        <div data-testid="config-table">
        <Table
          header={
            <Header
              variant="h1"
              description="Manage production line program configuration and target settings"
              counter={<span data-testid="config-program-count">{`(${visiblePrograms.length})`}</span>}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {/* Refresh Button */}
                  <span data-testid="config-btn-refresh">
                    <Button
                      variant="icon"
                      iconName="refresh"
                      onClick={() =>{dispatch(getProgramsRequest()),setFailedProgramIds(new Set()),setBatchFailureFlashItems([]);}}
                      ariaLabel="Refresh programs"
                      loading={programsLoading}
                    />
                  </span>

                  <span data-testid="config-btn-actions">
                    <ButtonDropdown
                      items={[
                        {
                          id: "enable",
                          text: "Enable",
                          disabled: selectedItems.length === 0,
                        },
                        {
                          id: "disable",
                          text: "Disable",
                          disabled: selectedItems.length === 0,
                        },
                      ]}
                      onItemClick={({ detail }) =>
                        handleActionClick(detail.id)
                      }
                      disabled={!canActionSelected || updateStatusLoading}
                    >
                      Actions
                    </ButtonDropdown>
                  </span>

                  <span data-testid="config-btn-update">
                    <Button
                      variant="normal"
                      disabled={!canUpdateSelected}
                      onClick={handleUpdate}
                    >
                      Update
                    </Button>
                  </span>

                  <span data-testid="config-btn-onboard">
                    <Button
                      variant="primary"
                      iconName="add-plus"
                      disabled={!hasPermission("configuration_setting.cs_onboard_program")}
                      onClick={handleOnboardProgram}
                    >
                      Onboard Program
                    </Button>
                  </span>
                </SpaceBetween>
              }
            >
              <span data-testid="config-page-title">Program Configuration</span>
            </Header>
          }
          columnDefinitions={visibleColumnDefinitions}
          items={paginatedPrograms}
          loading={programsLoading}
          loadingText="Loading programs..."
          selectionType="multi"
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) =>
            setSelectedItems(detail.selectedItems)
          }
          isItemDisabled={(item) => {
            const record = { cm: item.cm, location: item.cm_location, program: item.program_name, operation: item.operation };
            return !hasDataPermission("configuration_setting", "cs_update", record)
              && !hasDataPermission("configuration_setting", "cs_actions", record);
          }}
          trackBy="program_id"
          filter={
            <span data-testid="config-search-input">
              <TextFilter
                filteringText={filterText}
                filteringPlaceholder="Search by Program, Sub-Programs, CM, Location, Operation, or Product Family"
                onChange={({ detail }) => {
                  setFilterText(detail.filteringText);
                  setCurrentPage(1);
                }}
              />
            </span>
          }
          pagination={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span data-testid="config-pagination">
                <Pagination
                  currentPageIndex={currentPage}
                  pagesCount={totalPages || 1}
                  onChange={({ detail }) =>
                    setCurrentPage(detail.currentPageIndex)
                  }
                />
              </span>
              <span style={{ width: "1px", height: "20px", backgroundColor: "#e9ebed", flexShrink: 0 }} />
              <span data-testid="config-btn-column-settings">
                <Button
                  variant="icon"
                  iconName="settings"
                  onClick={handleOpenSettings}
                  ariaLabel="Column settings"
                />
              </span>
            </div>
          }
          empty={
            <Box textAlign="center" color="inherit">
              <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                <b>No programs</b>
              </Box>
              <Box variant="p" color="inherit">
                No programs to display. Click "Onboard Program" to add one.
              </Box>
            </Box>
          }
          sortingColumn={sortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn);
            setSortingDescending(detail.isDescending ?? false);
            setCurrentPage(1);
          }}
          variant="full-page"
          stickyHeader={false}
        />
        </div>
      </SpaceBetween>
    </div>
  );
};

export default ConfigurationSetting;
